import PropTypes from 'prop-types'
import Modal from '@/components/Modal'
import CardConfigure from '@/components/Modal/CardConfigure'
import Button from '@/components/Button'
import RedInfoIcon from '@/assets/svgs/red-info-icon.svg?react'

const REJECT_REASON_MAX = 1000

// The boxed, rounded icon treatment reads fine regardless of copy, so both
// presentations share it — only the title, body text, reason label/placeholder,
// and helper row differ between EMS and the two-stage wording.
const ICON_CLASS =
  'flex items-center justify-center w-12 h-12 rounded-[10px] shrink-0 bg-red-95 text-red-50 [&_svg]:w-6.5 [&_svg]:h-6.5'

// EMS's Reject button is single-stage, so it keeps the exact title, copy,
// reason label/placeholder, and two-line helper row EMS has always shipped.
// Only an element with `secondaryApproval.required` gets the newer wording,
// whose helper text is the character count alone.
const present = secondaryApproval =>
  secondaryApproval?.required
    ? {
        title: 'Reject Submission',
        body: 'Please enter a reason for rejecting the submission. The contractors will be notified of your reason for rejection.',
        reasonLabel: 'Reason for Rejection',
        placeholder: 'Describe your reason here',
      }
    : {
        title: 'Reject Request',
        body: 'This action will deny data sharing to the designated recipient. Proceed anyway?',
        reasonLabel: 'Reject Reason',
        placeholder: 'Enter Text Here',
      }

export const RejectModal = ({
  visible,
  onClose,
  onConfirm,
  rejectReason,
  setRejectReason,
  secondaryApproval,
}) => {
  const { title, body, reasonLabel, placeholder } = present(secondaryApproval)
  const isTwoStage = Boolean(secondaryApproval?.required)

  return (
    <Modal visible={visible} close={onClose} variant="small" dataTestId="submittal-reject-modal">
      <CardConfigure>
        <CardConfigure.Header>
          <CardConfigure.Header.Title>
            <div className="flex items-center w-full">
              <div className="flex items-center">
                <div className={ICON_CLASS}>
                  <RedInfoIcon />
                </div>
                <div className="ml-4">
                  <div className="font-avenir-heavy type-header-4 text-grey-30">{title}</div>
                </div>
              </div>
            </div>
          </CardConfigure.Header.Title>
        </CardConfigure.Header>
        <CardConfigure.Body>
          <CardConfigure.Body.Col>
            <p className="font-avenir-medium type-body-2 text-grey-40 mb-0 text-center">{body}</p>
            <div className="mt-5">
              <div className="flex items-center gap-0.5 font-avenir-medium type-meta-medium text-grey-50 mb-2">
                <span className="text-red-50">*</span>
                {reasonLabel}
              </div>
              <textarea
                className="w-full box-border resize-none border border-grey-90 rounded-lg p-3 font-avenir-medium type-body-2 text-grey-30 outline-none transition-[border-color] duration-150 placeholder:text-grey-80 focus:border-theme-50"
                rows={4}
                maxLength={REJECT_REASON_MAX}
                placeholder={placeholder}
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
              {isTwoStage ? (
                <div className="font-avenir-medium type-meta-medium text-grey-50 text-right mt-1">
                  {rejectReason.length}/{REJECT_REASON_MAX}
                </div>
              ) : (
                <div className="flex justify-between items-start mt-1">
                  <div className="font-avenir-medium type-meta-medium text-grey-50">
                    Describe reject reason here
                  </div>
                  <div className="font-avenir-medium type-meta-medium text-grey-50 text-right">
                    {rejectReason.length}/{REJECT_REASON_MAX}
                  </div>
                </div>
              )}
            </div>
          </CardConfigure.Body.Col>
        </CardConfigure.Body>
        <CardConfigure.Footer>
          <CardConfigure.Footer.Left>
            <Button
              data-testid="submittal-reject-cancel-btn"
              round
              btnText
              textWarning
              onClick={onClose}
            >
              Cancel
            </Button>
          </CardConfigure.Footer.Left>
          <CardConfigure.Footer.Right>
            <Button
              data-testid="submittal-reject-confirm-btn"
              round
              error
              disabled={rejectReason.trim().length === 0}
              onClick={onConfirm}
            >
              Confirm
            </Button>
          </CardConfigure.Footer.Right>
        </CardConfigure.Footer>
      </CardConfigure>
    </Modal>
  )
}

RejectModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  rejectReason: PropTypes.string.isRequired,
  setRejectReason: PropTypes.func.isRequired,
  // The element's workflow — same shape SubmittalBanner, ApproveModal, and the
  // status tag resolve against. Gates which presentation renders: see
  // `present` above.
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
}
